import React, { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Title from "../../../../components/Title/Title";
import Formula from "../../../../components/Formula/Formula";
import PolynomialFormula from "../../../../types/PolynominalFormula/PolynominalFormula";
import { InputCoefficientStyle } from "../../../../styles/styles";
import ArticleBorder from "../../../../components/ArticleBorder/ArticleBorder";
import ValueListDisplay from "../../../../components/ValueListDisplay/ValueListDisplay";
import FormulaListExplanation from "../../../../components/FormulaListExplanation/FormulaListExplanation";
interface FormData {
    degree: number;
    coefficients: string[];
}

const parseCoefficients = (coeffs: string[]): number[] =>
    coeffs.map((val) => (val === "" || isNaN(Number(val)) ? 0 : parseFloat(val)));

const PolynomialDegreeSelector: FC = () => {
    const { register, watch, setValue } = useForm<FormData>({
        defaultValues: {
            degree: 2,
            coefficients: [],
        },
    });

    const degree = watch("degree");
    const watchAll = watch();

    const [polynomialFormula, setPolynomialFormula] = useState<PolynomialFormula>(new PolynomialFormula([0, 0]));
    const [latex, setLatex] = useState<string>("");

    useEffect(() => {
        setValue("coefficients", Array(degree + 1).fill(""), { shouldValidate: true });
    }, [degree, setValue]);

    useEffect(() => {
        const rawCoefficients = watchAll.coefficients;
        if (rawCoefficients && Array.isArray(rawCoefficients)) {
            try {
                const numericCoefficients = parseCoefficients(rawCoefficients);
                const formula = new PolynomialFormula(numericCoefficients);
                setPolynomialFormula(formula);
                setLatex(formula.toLatex());
            } catch (error) {
                console.error("B\u0142\u0105d podczas generowania wzoru:", error);
            }
        }
    }, [watchAll, degree]);

    return (
        <>
            <Title text="Kalkulator funkcji wielomianowej" type="main-article" />

            <form className="flex flex-col gap-4 max-w-md mx-auto">
                <label className="flex flex-col">
                    <div className="InputsWrapper flex flex-col flex-wrap items-center mt-2">
                        <span>Wybierz stopień wielomianu:</span>
                        <select {...register("degree", { valueAsNumber: true })} className={InputCoefficientStyle}>
                            {[...Array(9)].map((_, index) => (
                                <option key={index + 2} value={index + 2}>
                                    {index + 2}
                                </option>
                            ))}
                        </select>
                    </div>
                </label>

                <div className="flex flex-col items-center gap-2">
                    <span>Wprowadź współczynniki:</span>
                    {[...Array(degree + 1)].map((_, i) => {
                        const power = degree - i;
                        return (
                            <div key={power} className="flex items-center gap-2">
                                <label className="w-full flex justify-center">
                                    <input
                                        type="number"
                                        step="any"
                                        {...register(`coefficients.${i}`)}
                                        className={InputCoefficientStyle}
                                    />
                                    <span className="block w-3">
                                        <b>x<sup>{power}</sup></b>
                                    </span>
                                </label>
                            </div>
                        );
                    })}
                </div>
            </form>

            {latex && (
                <>
                    <ArticleBorder />
                    <div className="text-center">
                        <p>Wprowadzony wielomian:</p>
                        <Formula formula={`f(x) = ${latex}`} />

                        <ArticleBorder />

                        <ValueListDisplay
                            title="Dzielniki wyrazu wolnego a₀"
                            values={polynomialFormula.getDividersA0WithMinuses()}
                        />

                        <p className="mt-4 font-bold">Znalezione pierwiastki całkowite wielomianu:</p>
                        {polynomialFormula.getPolynomialRootsFromDividers().length > 0 ? (
                            <ValueListDisplay
                                values={polynomialFormula.getPolynomialRootsFromDividers()}
                                bgColor="bg-white-100"
                                textColor="text-black"
                            />
                        ) : (
                            <p className="text-gray-500 mt-2">Brak pierwiastków całkowitych.</p>
                        )}

                        <FormulaListExplanation
                            title="Szczegółowe obliczenia"
                            lines={polynomialFormula.evaluateAtDividersWithMinuses()}
                        />

                        <ArticleBorder />

                        <ValueListDisplay
                            title={<>Dzielniki współczynnika przy największej potędze a<sub>{degree}</sub></>}
                            values={polynomialFormula.getDividersAnWithMinuses()}
                        />

                        <ValueListDisplay
                            title="Zbiór potencjalnych pierwiastków wymiernych wielomianu"
                            values={polynomialFormula.getAllPossibleRationalRootsAsReducedStrings()}
                            bgColor="bg-green-100"
                            textColor="text-green-800"
                        />

                        <p className="mt-4 font-bold">Znalezione pierwiastki wymierne wielomianu:</p>
                        {polynomialFormula.getPolynomialRationalRoots().length > 0 ? (
                            <ValueListDisplay
                                values={polynomialFormula.getPolynomialRationalRoots()}
                                bgColor="bg-white-100"
                                textColor="text-black"
                            />
                        ) : (
                            <p className="text-gray-500 mt-2">Brak pierwiastków wymiernych.</p>
                        )}

                        <FormulaListExplanation
                            title="Szczegółowe obliczenia"
                            lines={polynomialFormula.evaluateAtAllRationalRoots()}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default PolynomialDegreeSelector;
